import { Theme, createTheme } from '@material-ui/core';
import tinycolor from 'tinycolor2';
import { ThemeCustomization, ThemeDefault } from '../types/api';

const hexToRgb = (hex: string) => {
	let input = hex.replace('#', '').toUpperCase();
	const hexRegex = /[0-9A-F]/g;
	if (!hexRegex.test(input) || (input.length !== 3 && input.length !== 6)) {
		throw new Error('input is not a valid hex color.');
	}
	if (input.length === 3) {
		input = input[0] + input[0] + input[1] + input[1] + input[2] + input[2];
	}
	const first = input[0] + input[1];
	const second = input[2] + input[3];
	const last = input[4] + input[5];

	return `${parseInt(first, 16)}, ${parseInt(second, 16)}, ${parseInt(
		last,
		16
	)}`;
};

export const hexAlpha = (hex: string, alpha: number = 1) =>
	`rgba(${hexToRgb(hex)}, ${alpha})`;

export const colors = {
	white: '#ffffff',
	black: '#000000',
	red: '#D63541',
	blue: '#0463C2',
	blueA80: '#3682CE',
	blueA50: '#81B1E0',
	blueA20: '#CEE1F3',
	grayA80: '#5C5C5C',
	grayA50: '#7F7F7F',
	grayA20: '#CCCCCC',
	grayA10: '#E7E7E7',
	grayA05: '#f5f5f5',
};

export const mobiversTheme = (customizing?: ThemeCustomization): Theme => {
	const customization: ThemeDefault = {
		...defaultTheme,
		...customizing,
		colors: {
			...defaultTheme.colors,
			...customizing?.colors,
		},
	};

	const primaryLightColor = tinycolor(customization.colors.primary);
	primaryLightColor.setAlpha(0.15);
	const secondaryLightColor = tinycolor(customization.colors.secondary);
	secondaryLightColor.setAlpha(0.15);

	return createTheme({
		palette: {
			primary: {
				main: customization.colors.primary,
				light: primaryLightColor.toString(),
				contrastText: customization.colors.primaryContrast,
			},
			secondary: {
				main: customization.colors.secondary,
				light: secondaryLightColor.toString(),
				contrastText: customization.colors.secondaryContrast,
			},
			error: { main: colors.red, contrastText: colors.white },
			// warning: purple,
			// info: {},
			// success: {},
			// text: {primary: colors.blue, secondary: colors.grayA50, disabled: colors.grayA80}
		},
		typography: {
			fontFamily: ['din-2014', 'sans-serif'].join(','),
			h1: {
				fontSize: 36,
				lineHeight: '48px',
				fontWeight: 600, // demibold
			},
			h2: {
				fontSize: 26,
				lineHeight: '34px',
				fontWeight: 600,
			},
			h3: {
				fontSize: 18,
				lineHeight: '24px',
				fontWeight: 600,
			},
			h4: {
				fontSize: 18,
				lineHeight: '22px',
				fontWeight: 400,
			},
			h5: {
				fontSize: 16,
				lineHeight: '22px',
				fontWeight: 600,
			},
			h6: {
				fontSize: 16,
				lineHeight: '22px',
				fontWeight: 600,
			},
			subtitle1: {
				fontSize: 16,
				lineHeight: '22px',
				fontWeight: 600,
			},
			subtitle2: {
				fontSize: 14,
				lineHeight: '20px',
				fontWeight: 600,
			},
			body1: {
				fontSize: 16,
				lineHeight: '22px',
				fontWeight: 400, // regular
			},
			body2: {
				fontSize: 14,
				lineHeight: '20px',
				fontWeight: 400,
			},
			button: {
				fontSize: 16,
				lineHeight: '20px',
				fontWeight: 600,
				textTransform: 'initial',
			},
			caption: {
				fontSize: 12,
				lineHeight: '18px',
				fontWeight: 400,
			},
			overline: {
				fontSize: 12,
				lineHeight: '18px',
				fontWeight: 400,
			},
		},
	});
};

const defaultTheme: ThemeDefault = {
	colors: {
		primary: colors.blue,
		primaryContrast: colors.white,
		secondary: colors.blueA80,
		secondaryContrast: colors.white,
	},
};
